import React, { useContext, useCallback, useMemo, useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import styled from 'styled-components';
import Container from 'ls-common-client/dist/components/Container';
import Animation from 'ls-common-client/dist/components/Animation';
import ScrollContainer from '../../components/UserManagement/ScrollContainer';
import SideBar from '../../components/UserManagement/SideBar';
import UserEmptyLayout from '../../components/UserManagement/UserEmptyLayout';
import { MODALS } from '../../constants';
import { Context } from '../../context/AppContext';
import UserEnquiriesGraphic from '../../assets/userManagement/userEnquiriesGraphic.png';
import UserEnquiryIcon from '../../assets/userManagement/userEnquiryIcon.png';
import UserEnquiriesContact from '../../assets/userManagement/userEnquiriesContact.png';
import UserEnquiriesGraphicWebp from '../../assets/userManagement/userEnquiriesGraphic.webp';
import listviewLoader from '../../animations/listviewLoader.json';
import { userEnquiriesQuery } from '../../gql/enquiries';
import getNodes from '../../lib/getNodes';
import Enquiry from './Enquiry';

const StyledContainer = styled(Container)`
  margin-top: 58px;
  padding: 0 30px;
  flex: 1;
  h1 {
    font-size: 30px;
    margin-bottom: 41px;
    line-height: 1.3em;
    font-weight: medium;
    @media (max-width: 767px) {
      font-size: 22px;
      margin-bottom: 20px;
    }
  }
  @media (max-width: 767px) {
    margin-top: 27px;
    padding: 0 11px;
  }
`;

const PAGE_SIZE = 10;

const Enquiries = () => {
  const {
    user: { user, loading: userLoading },
    media: { mobile, desktop },
    modal: { showModal },
  } = useContext(Context);

  const { id: userId } = user || {};

  const [fetchEnquiries, { data, loading, fetchMore }] = useLazyQuery(
    userEnquiriesQuery,
    {
      errorPolicy: 'all',
      variables: {
        userId,
        first: PAGE_SIZE,
      },
      notifyOnNetworkStatusChange: true,
    }
  );

  const { enquiries } = data || {};
  const { pageInfo, edges } = enquiries || {};
  const { endCursor, hasNextPage } = pageInfo || {};

  useEffect(() => {
    if (userId) {
      fetchEnquiries();
    }
  }, [userId]);

  const onNextPage = useCallback(async () => {
    if (hasNextPage && endCursor && !loading) {
      await fetchMore({
        variables: {
          userId,
          first: PAGE_SIZE,
          after: endCursor,
        },
      });
    }
  }, [endCursor, userId, hasNextPage, loading, fetchMore]);

  const list = useMemo(() => getNodes(edges || []) || [], [edges]);

  if (userLoading) {
    return (
      <Container display="flex" justifyContent="center" alignItems="center">
        <Animation data={listviewLoader} width="300px" />
      </Container>
    );
  }

  return (
    <>
      {!loading && !list.length && (
        <UserEmptyLayout
          heading={`You haven’t submitted any ${
            mobile ? 'business ' : ''
          }enquiries
        yet.`}
          text="Search for businesses now to get started."
          buttonText="Start a Search"
          onClick={() =>
            showModal(MODALS.SEARCH_AUTOSUGGEST, {
              inputFocus: 'location',
            })
          }
          graphic={UserEnquiriesGraphicWebp}
          graphicFallback={UserEnquiriesGraphic}
        />
      )}
      <StyledContainer>
        <h1>{mobile ? '' : 'Your '}Enquiries</h1>
        <Container display={!desktop ? 'block' : 'flex'}>
          <ScrollContainer
            id="UserEnquiries__list"
            onScrollToBottom={() => onNextPage()}
          >
            {list.map(enquiry => (
              <Enquiry key={enquiry.id} enquiry={enquiry} />
            ))}
            {loading && (
              <Container
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Animation data={listviewLoader} width="300px" />
              </Container>
            )}
          </ScrollContainer>
          <SideBar
            items={[
              {
                title: 'Need to update your enquiry request?',
                icon: UserEnquiryIcon,
                iconWidth: '50',
                children: (
                  <p>
                    While you won&apos;t be able to update the enquiry you just
                    sent, we recommend getting in direct contact with the
                    business.
                  </p>
                ),
              },
              {
                title: 'How will the business get in touch?',
                icon: UserEnquiriesContact,
                iconWidth: '47',
                children: (
                  <p>
                    Once you submit an enquiry, the business will reach out to
                    you via text, phone, or email.
                  </p>
                ),
              },
            ]}
          />
        </Container>
      </StyledContainer>
    </>
  );
};

export default Enquiries;
